/**
 * WarningConfirmDialog.
 *
 * Re-usable modal for the Pass 23 admin warning retry pattern. The server
 * throws a `WARNING_*` error when an admin attempt would do something the
 * spec wants confirmed (reducing partQuantity below ordered, deleting a
 * part with billing activity). The UI catches the error, detects the code
 * via `detectAdminWarning`, and shows this dialog. Confirming retries the
 * mutation with the appropriate `acknowledge*=true` flag.
 *
 * The dialog is intentionally generic: the parent component owns the
 * mutation and the retry call. This component only renders the explanation
 * and confirm/cancel buttons.
 */

import { AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';

interface WarningConfirmDialogProps {
  open: boolean;
  /** The human-readable warning text from the server (prefix already stripped). */
  message: string;
  /** Title shown in the modal header. Defaults to "Confirm". */
  title?: string;
  /** Label for the destructive button. Defaults to "Confirm and proceed". */
  confirmLabel?: string;
  /** Mutation in-flight indicator. */
  pending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function WarningConfirmDialog({
  open,
  message,
  title = 'Confirm',
  confirmLabel = 'Confirm and proceed',
  pending = false,
  onCancel,
  onConfirm,
}: WarningConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} loading={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-warning-strong"
          strokeWidth={1.5}
          aria-hidden
        />
        <p className="text-sm text-secondary">{message}</p>
      </div>
    </Modal>
  );
}
